import { Box, Typography } from "@mui/material";
import { grey } from "@mui/material/colors";

const formatQty = (value) => Number(value || 0).toLocaleString("en-IN");

const safeNumber = (value) => {
	const parsed = Number(value);
	return Number.isFinite(parsed) ? parsed : 0;
};

const label = (value, fallback) => String(value ?? "").trim() || fallback;

const uniqueInOrder = (values) => {
	const seen = new Set();
	const result = [];
	values.forEach((value) => {
		if (!value || seen.has(value)) return;
		seen.add(value);
		result.push(value);
	});
	return result;
};

const emptySizeMap = (sizes) => Object.fromEntries(sizes.map((size) => [size, 0]));

const addRowQty = (target, size, qty) => {
	if (!Object.prototype.hasOwnProperty.call(target, size)) target[size] = 0;
	target[size] += qty;
};

const rowTotal = (sizeMap, sizes) => sizes.reduce((acc, size) => acc + safeNumber(sizeMap[size]), 0);

const buildSizeColumns = (rows, sizes = []) => {
	const fromStyle = (sizes || []).map((size) => label(size, "")).filter(Boolean);
	const fromRows = uniqueInOrder(rows.map((row) => label(row.size, "")));
	return uniqueInOrder([...fromStyle, ...fromRows]);
};

const buildChannelPivot = (rows, sizes) => {
	const channels = uniqueInOrder(rows.map((row) => label(row.channel, "-")));
	const totals = emptySizeMap(sizes);
	const byChannel = new Map(channels.map((channel) => [channel, emptySizeMap(sizes)]));

	rows.forEach((row) => {
		const channel = label(row.channel, "-");
		const size = label(row.size, "-");
		const qty = safeNumber(row.total_qty);
		if (!byChannel.has(channel)) byChannel.set(channel, emptySizeMap(sizes));
		addRowQty(byChannel.get(channel), size, qty);
		addRowQty(totals, size, qty);
	});

	const sortedChannels = [...byChannel.keys()].sort((a, b) => {
		const qtyDiff = rowTotal(byChannel.get(b), sizes) - rowTotal(byChannel.get(a), sizes);
		return qtyDiff || a.localeCompare(b);
	});

	return [
		{ key: "total", label: "Total", kind: "total", qty: totals },
		...sortedChannels.map((channel) => ({
			key: channel,
			label: channel,
			kind: "channel",
			qty: byChannel.get(channel),
		})),
	];
};

const buildRegionPivot = (rows, sizes) => {
	const channelOrder = uniqueInOrder(rows.map((row) => label(row.channel, "-")));
	const regionsByChannel = new Map();
	const fullTotal = emptySizeMap(sizes);
	const channelTotals = new Map();
	const detail = new Map();

	channelOrder.forEach((channel) => {
		channelTotals.set(channel, emptySizeMap(sizes));
		regionsByChannel.set(channel, []);
	});

	rows.forEach((row) => {
		const channel = label(row.channel, "-");
		const region = label(row.region, "ALL");
		const size = label(row.size, "-");
		const qty = safeNumber(row.total_qty);
		const detailKey = `${channel}__${region}`;

		if (!channelTotals.has(channel)) {
			channelOrder.push(channel);
			channelTotals.set(channel, emptySizeMap(sizes));
			regionsByChannel.set(channel, []);
		}
		if (!detail.has(detailKey)) {
			detail.set(detailKey, emptySizeMap(sizes));
			regionsByChannel.get(channel).push(region);
		}

		addRowQty(detail.get(detailKey), size, qty);
		addRowQty(channelTotals.get(channel), size, qty);
		addRowQty(fullTotal, size, qty);
	});

	const pivotRows = [{ key: "full-total", label: "Total", kind: "total", qty: fullTotal }];

	const sortedChannels = [...channelTotals.keys()].sort((a, b) => {
		const qtyDiff = rowTotal(channelTotals.get(b), sizes) - rowTotal(channelTotals.get(a), sizes);
		return qtyDiff || a.localeCompare(b);
	});

	sortedChannels.forEach((channel) => {
		pivotRows.push({
			key: `${channel}__total`,
			label: `${channel} Total`,
			kind: "channel-total",
			qty: channelTotals.get(channel),
		});
		uniqueInOrder(regionsByChannel.get(channel) || [])
			.sort((a, b) => {
				const qtyDiff =
					rowTotal(detail.get(`${channel}__${b}`) || {}, sizes) - rowTotal(detail.get(`${channel}__${a}`) || {}, sizes);
				return qtyDiff || a.localeCompare(b);
			})
			.forEach((region) => {
				pivotRows.push({
					key: `${channel}__${region}`,
					label: `${channel} · ${region}`,
					kind: "detail",
					qty: detail.get(`${channel}__${region}`),
				});
			});
	});

	return pivotRows;
};

const headCellSx = (extra = {}) => ({
	px: 1.75,
	py: 1,
	fontSize: 13,
	fontWeight: 700,
	color: grey[500],
	borderBottom: `1px solid ${grey[200]}`,
	bgcolor: grey[50],
	whiteSpace: "nowrap",
	position: "sticky",
	top: 0,
	zIndex: 2,
	...extra,
});

const bodyCellSx = (extra = {}) => ({
	px: 1.75,
	py: 1,
	fontSize: 15,
	borderBottom: `1px solid ${grey[100]}`,
	fontVariantNumeric: "tabular-nums",
	whiteSpace: "nowrap",
	...extra,
});

const rowTone = (kind) => {
	if (kind === "total") return { bgcolor: grey[900], color: "#fff", fontWeight: 800 };
	if (kind === "channel-total") return { bgcolor: grey[100], fontWeight: 800 };
	if (kind === "channel") return { fontWeight: 700 };
	return { fontWeight: 600 };
};

const QtyRowsSummary = ({ rows = [], sizes = [], buyLevel = "CHANNEL", emptyMessage = "No qty to summarize yet." }) => {
	const sizeCols = buildSizeColumns(rows, sizes);
	const isRegionLevel = buyLevel === "REGION";
	const pivotRows = isRegionLevel ? buildRegionPivot(rows, sizeCols) : buildChannelPivot(rows, sizeCols);
	const totalQty = rows.reduce((acc, row) => acc + safeNumber(row.total_qty), 0);

	if (!rows.length) {
		return (
			<Box sx={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center", px: 2 }}>
				<Typography variant="body2" color={grey[500]}>
					{emptyMessage}
				</Typography>
			</Box>
		);
	}

	return (
		<Box sx={{ p: 1.5, height: "100%", boxSizing: "border-box", display: "flex", flexDirection: "column", minHeight: 0 }}>
			<Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", px: 0.25, mb: 1.25, flexShrink: 0 }}>
				<Typography sx={{ fontSize: 13, fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase", color: grey[500] }}>
					{isRegionLevel ? "Channel · Region size qty" : "Channel size qty"}
				</Typography>
				<Typography sx={{ fontSize: 20, fontWeight: 800, fontVariantNumeric: "tabular-nums" }}>{formatQty(totalQty)}</Typography>
			</Box>
			<Box sx={{ flex: 1, minHeight: 0, overflow: "auto", border: `1px solid ${grey[200]}`, borderRadius: 2 }}>
				<Box component="table" sx={{ width: "100%", borderCollapse: "collapse", minWidth: 560 }}>
					<Box component="thead">
						<Box component="tr">
							<Box
								component="th"
								sx={headCellSx({
									textAlign: "left",
									left: 0,
									zIndex: 3,
									minWidth: isRegionLevel ? 180 : 132,
								})}>
								{isRegionLevel ? "Channel / Region" : "Channel"}
							</Box>
							{sizeCols.map((size) => (
								<Box component="th" key={size} sx={headCellSx({ textAlign: "right" })}>
									{size}
								</Box>
							))}
							<Box component="th" sx={headCellSx({ textAlign: "right" })}>
								Total
							</Box>
						</Box>
					</Box>
					<Box component="tbody">
						{pivotRows.map((row) => {
							const tone = rowTone(row.kind);
							const isTotal = row.kind === "total";
							const cellColor = isTotal ? "#fff" : grey[900];
							const mutedColor = isTotal ? "rgba(255,255,255,0.72)" : grey[400];
							return (
								<Box component="tr" key={row.key}>
									<Box
										component="td"
										sx={bodyCellSx({
											...tone,
											position: "sticky",
											left: 0,
											zIndex: 1,
											textAlign: "left",
											pl: row.kind === "detail" ? 2.75 : 1.75,
										})}>
										{row.label}
									</Box>
									{sizeCols.map((size) => {
										const qty = safeNumber(row.qty[size]);
										return (
											<Box
												component="td"
												key={`${row.key}-${size}`}
												sx={bodyCellSx({
													...tone,
													textAlign: "right",
													color: qty ? cellColor : mutedColor,
												})}>
												{qty ? formatQty(qty) : "—"}
											</Box>
										);
									})}
									<Box
										component="td"
										sx={bodyCellSx({
											...tone,
											textAlign: "right",
											color: cellColor,
										})}>
										{formatQty(rowTotal(row.qty, sizeCols))}
									</Box>
								</Box>
							);
						})}
					</Box>
				</Box>
			</Box>
		</Box>
	);
};

export default QtyRowsSummary;
